

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Checkbox,
  Divider,
  Paper,
  InputAdornment,
  Popper,
  Grow,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { v4 as uuidv4 } from 'uuid';

// ====================================================================================
// IMPORTANT - PLEASE READ
// ====================================================================================
// To fix the "No results found" bug, the component that USES this LabelFilterMenu
// MUST pass the FULL, UNFILTERED list of all possible keys in the `labelKeys` prop.
//
// Do NOT pre-filter the `labelKeys` array in the parent component. This component
// is designed to handle all the necessary filtering internally.
//
// CORRECT PARENT USAGE:
// const allPossibleKeys = ['region', 'env', 'os', 'batch-job-uid'];
// return <LabelFilterMenu labelKeys={allPossibleKeys} ... />;
//
// ====================================================================================


export interface LabelFilterPair {
  id: string;
  key: string;
  values: string[];
  source?: 'existing' | 'manual';
}

interface NewLabel {
    id: string;
    key:string;
    value: string;
}

interface LabelFilterMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: LabelFilterPair[]) => void;
  labelKeys: (string | { [key: string]: any })[];
  labelValues: { [key: string]: string[] };
  onFetchLabelValues: (key: string) => void;
  appliedFilters: LabelFilterPair[];
  loadingLabelKey: string | null;
}

const getOptionText = (option: string | { [key: string]: any }): string => {
    if (typeof option === 'string') return option;
    if (option && typeof option === 'object') return String(option.name || option.value || '');
    return '';
};

const LabelFilterMenu: React.FC<LabelFilterMenuProps> = ({
  open,
  anchorEl,
  onClose,
  onApply,
  labelKeys,
  labelValues,
  onFetchLabelValues,
  appliedFilters,
  loadingLabelKey,
}) => {
  const [tempFilters, setTempFilters] = useState<LabelFilterPair[]>([]);
  const [newLabels, setNewLabels] = useState<NewLabel[]>([{ id: uuidv4(), key: '', value: '' }]);

  // Final, robust initialization logic
  useEffect(() => {
    if (open) {
        const allApplied = JSON.parse(JSON.stringify(appliedFilters || []));
        const appliedAsNew: NewLabel[] = [];
        const appliedAsExisting: LabelFilterPair[] = [];

        allApplied.forEach((filter: LabelFilterPair) => {
            if (filter.source === 'existing') {
                appliedAsExisting.push(filter);
            } else if (filter.values.length > 0) {
                appliedAsNew.push({ id: filter.id, key: filter.key, value: filter.values[0] });
            }
        });

        setNewLabels(appliedAsNew.length > 0 ? appliedAsNew : [{ id: uuidv4(), key: '', value: '' }]);

        // Always start with the already applied filters...
        let initialFilters = [...appliedAsExisting];
        
        // ...and ALWAYS add one blank row for the user to work with.
        // This ensures the UI is always ready for a new entry.
        initialFilters.push({ id: uuidv4(), key: '', values: [], source: 'existing' });
        
        setTempFilters(initialFilters);
    }
  }, [open, appliedFilters]); // Dependency array simplified to run only when menu opens or filters are applied.

  useEffect(() => {
    if (!open) return;
    tempFilters.forEach(filter => {
      if (filter.key && !labelValues[filter.key] && loadingLabelKey !== filter.key) {
        onFetchLabelValues(filter.key);
      }
    });
  }, [open, tempFilters, labelValues, loadingLabelKey, onFetchLabelValues]);


  const handleAddNewLabel = () => {
    setNewLabels([...newLabels, { id: uuidv4(), key: '', value: '' }]);
  };

  const handleDeleteNewLabel = (id: string) => {
    setNewLabels(newLabels.filter(l => l.id !== id));
  };

  const handleUpdateNewLabel = (id: string, field: 'key' | 'value', text: string) => {
    setNewLabels(newLabels.map(l => (l.id === id ? { ...l, [field]: text } : l)));
  };

  const handleAddMore = () => {
    setTempFilters([...tempFilters, { id: uuidv4(), key: '', values: [], source: 'existing' }]);
  };

  const handleDelete = (id: string) => {
    // Prevent removing the very last row, reset it instead.
    if (tempFilters.length <= 1) {
        setTempFilters([{ id: uuidv4(), key: '', values: [], source: 'existing' }]);
    } else {
        setTempFilters(tempFilters.filter(f => f.id !== id));
    }
  };

  const handleUpdateFilter = (id: string, newFilter: Partial<LabelFilterPair>) => {
    setTempFilters(tempFilters.map(f => (f.id === id ? { ...f, ...newFilter, source: 'existing' } : f)));
  };

  const handleApplyClick = () => {
    const validNewLabels = newLabels
        .filter(l => l.key.trim() && l.value.trim())
        .map(l => ({ id: l.id, key: l.key.trim(), values: [l.value.trim()], source: 'manual' as const }));
        
    const validExistingFilters = tempFilters.filter(f => f.key && f.values.length > 0);
    
    onApply([...validNewLabels, ...validExistingFilters]);
    onClose();
  };

  const handleClearClick = () => {
    setTempFilters([{ id: uuidv4(), key: '', values: [], source: 'existing' }]);
    setNewLabels([{ id: uuidv4(), key: '', value: '' }]);
    onApply([]);
  };

  const headerSx = {
    fontWeight: 600,
    fontSize: '14px',
    color: '#666666',
    bgcolor: '#F9FAFB',
    p: 1,
    mx: -2,
    mb: 1,
  };

  // This logic now correctly filters the FULL list of labelKeys for each row.
  const usedKeys = tempFilters.map(f => f.key).filter(Boolean);
  const canAddMore = labelKeys.length > usedKeys.length;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ 'aria-labelledby': 'label-filter-button', disablePadding: true }}
      PaperProps={{
        elevation: 1,
        sx: { width: 650, p: 2, borderRadius: '12px', mt: 1 }
      }}
    >
      <Typography sx={headerSx}>Add Label</Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', backgroundColor: 'rgba(249, 250, 251, 1)', mt: 2, border: 'none' }}>
        <Stack spacing={2}>
          {newLabels.map((label) => (
            <Stack key={label.id} direction="row" spacing={1} alignItems="flex-end">
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 400, color: '#333333', fontSize: '14px' }}>
                      Key<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                      placeholder="Enter Key"
                      variant="outlined"
                      size="small"
                      value={label.key}
                      onChange={(e) => handleUpdateNewLabel(label.id, 'key', e.target.value)}
                      sx={{ flex: 1, backgroundColor: 'white', '& .MuiOutlinedInput-root': { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' }, borderRadius: '6px', height: '36px', fontSize: '13px' }}}
                  />
              </Stack>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 400, color: '#333333', fontSize: '14px' }}>
                      Value<span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <TextField
                      placeholder="Enter Value"
                      variant="outlined"
                      size="small"
                      value={label.value}
                      onChange={(e) => handleUpdateNewLabel(label.id, 'value', e.target.value)}
                      sx={{ flex: 1, backgroundColor: 'white', '& .MuiOutlinedInput-root': { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' }, borderRadius: '6px', height: '36px', fontSize: '13px' }}}
                  />
              </Stack>
              <IconButton onClick={() => handleDeleteNewLabel(label.id)} disabled={newLabels.length === 1}>
                <img src="/icons/delete_icon.svg" alt="Delete" style={{ width: 16, height: 16 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Paper>
      <Button onClick={handleAddNewLabel} sx={{ mt:0.5, textTransform: 'none', color: '#0064FF',fontSize:'12px', fontWeight: 500 }}>
        + Add More
      </Button>

      <>
          <Typography sx={{...headerSx, mt: 2}}>Existing Labels</Typography>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', backgroundColor: 'rgba(249, 250, 251, 1)', mt: 2, border: 'none' }}>
              <Stack spacing={2}>
              {tempFilters.map((filter) => {
                  // For each row, calculate which keys should be in its dropdown.
                  const availableKeysForThisRow = labelKeys.filter(keyOption => {
                      const keyText = getOptionText(keyOption);
                      // A key is available if it's NOT used by another row, OR if it IS the key for the current row.
                      return !usedKeys.includes(keyText) || keyText === filter.key;
                  });

                  // We don't render a row if its key is blank AND there are no keys to select from.
                  // This cleans up the UI by hiding the extra blank row if all keys have been used.
                  if (!filter.key && availableKeysForThisRow.length === 0) {
                    return null;
                  }

                  return (
                    <Stack key={filter.id} direction="row" spacing={1} alignItems="flex-end">
                      <SearchableDropdown
                          label={<>Key<span style={{ color: 'red' }}>*</span></>}
                          options={availableKeysForThisRow}
                          value={filter.key}
                          onChange={(newKey) => {
                            handleUpdateFilter(filter.id, { key: newKey as string, values: [] });
                          }}
                          searchPlaceholder="Search by Key.."
                      />
                      <SearchableDropdown
                          label={<>Value<span style={{ color: 'red' }}>*</span></>}
                          options={labelValues[filter.key] || []}
                          value={filter.values.length > 1 ? `${filter.values.length} selected` : filter.values.join(', ')}
                          multiple
                          selectedValues={filter.values}
                          onChange={(newValues) => handleUpdateFilter(filter.id, { values: newValues as string[] })}
                          disabled={!filter.key}
                          searchPlaceholder="Search by value.."
                          isFetching={loadingLabelKey === filter.key}
                      />
                      <IconButton onClick={() => handleDelete(filter.id)} disabled={tempFilters.length === 1}>
                          <img src="/icons/delete_icon.svg" alt="Delete" style={{ width: 16, height: 16 }} />
                      </IconButton>
                    </Stack>
                  );
              })}
              </Stack>
          </Paper>

          <Button onClick={handleAddMore} sx={{ mt:0.5, textTransform: 'none', color: '#0064FF',fontSize:'12px', fontWeight: 500 }} disabled={!canAddMore}>
              + Add More
          </Button>
      </>

      <Divider sx={{ mx: -2,mt:1,mb:1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
        <Button onClick={handleClearClick} variant="text" sx={{ textTransform: 'none', color: '#666666', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>Clear</Button>
        <Button onClick={handleApplyClick} variant="text" sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>Apply</Button>
      </Box>
    </Menu>
  );
};

// SearchableDropdown component (no changes needed)
const SearchableDropdown: React.FC<{
  label: React.ReactNode;
  options: (string | { [key: string]: any })[];
  value: string;
  onChange: (value: string | string[]) => void;
  searchPlaceholder: string;
  multiple?: boolean;
  selectedValues?: string[];
  disabled?: boolean;
  isFetching?: boolean;
}> = memo(({ label, options, value, onChange, multiple, selectedValues = [], disabled, searchPlaceholder, isFetching = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const open = Boolean(anchorEl);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const localGetOptionText = useCallback((option: string | { [key: string]: any }): string => {
    return getOptionText(option);
  }, []);

  const filteredOptions = options.filter(option =>
    localGetOptionText(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      const newSelection = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newSelection);
    } else {
      onChange(optionValue);
      handleCloseMenu();
    }
  };

  const handleSelectAll = () => {
    const allFilteredTexts = filteredOptions.map(localGetOptionText);
    const areAllSelected = allFilteredTexts.length > 0 && allFilteredTexts.every(opt => selectedValues.includes(opt));

    if (areAllSelected) {
      const newSelection = selectedValues.filter(val => !allFilteredTexts.includes(val));
      onChange(newSelection);
    } else {
        const newSelection = Array.from(new Set([...selectedValues, ...allFilteredTexts]));
      onChange(newSelection);
    }
  };

  return (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ fontWeight: 400, color: '#333333', fontSize: '14px' }}>
        {label}
      </Typography>
      <TextField
        placeholder={!multiple ? 'Select Key' : 'Select value'}
        value={value}
        onClick={handleOpenMenu}
        disabled={disabled}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {isFetching ? (
                <CircularProgress size={20} />
              ) : (
                <KeyboardArrowDownIcon sx={{ color: '#333333', transform: open ? 'rotate(180deg)' : 'rotate(0deg)',fontSize: '20px'}} />
              )}
            </InputAdornment>
          ),
          sx: { borderRadius: '6px', height: '36px', fontSize: '14px',backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF', border:' #E8EBEC' },
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0064FF' },
          },
            '& .MuiInputBase-input::placeholder': {
            color: '#999999', fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', lineHeight: '18px', letterSpacing: '0%', opacity: 1,
          },
        }}
      />
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1301, width: anchorEl?.offsetWidth }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <Paper elevation={3} sx={{ mt: 1, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              <ClickAwayListener onClickAway={handleCloseMenu}>
                <div>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB' }}>
                    <TextField
                      autoFocus
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ ml: -0.5 }}>
                            <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
                          </InputAdornment>
                        ),
                        sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
                      }}
                    />
                  </Box>

                  <Box sx={{ maxHeight: 210, overflowY: 'auto' }}>
                    {multiple && filteredOptions.length > 0 && (
                      <MenuItem>
                        <Checkbox
                            size="small"
                            checked={filteredOptions.length > 0 && filteredOptions.every(opt => selectedValues.includes(localGetOptionText(opt)))}
                            indeterminate={
                              !filteredOptions.every(opt => selectedValues.includes(localGetOptionText(opt))) &&
                              filteredOptions.some(opt => selectedValues.includes(localGetOptionText(opt)))
                            }
                            onChange={handleSelectAll}
                            sx={{ '&.Mui-checked': { color: '#0064FF' }, '&.Mui-indeterminate': { color: '#0064FF' } }}
                          />
                        All
                      </MenuItem>
                    )}

                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option, index) => {
                        const optionText = localGetOptionText(option);
                        return (
                          <MenuItem
                            key={`${optionText}-${index}`}
                            onClick={() => handleSelect(optionText)}
                            selected={!multiple && value === optionText}
                            sx={{ fontSize: '14px', fontWeight: 400,color:'#666666',height: '40px',fontFamily:'Inter'}}
                          >
                            {multiple && <Checkbox size="small"
                            sx={{ '&.Mui-checked': { color: '#0064FF' } }}
                            checked={selectedValues.includes(optionText)} />}
                            {optionText}
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled>No results found</MenuItem>
                    )}
                  </Box>

                  {multiple && (
                    <Box sx={{ px: 1, py: 0.5, borderTop: '1px solid #E5E7EB', textAlign: 'right'}}>
                    <Button onClick={handleCloseMenu} size="small" sx={{ textTransform: 'none',color: '#0064FF'  }}>
                          Done
                      </Button>
                    </Box>
                  )}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );
});

export default LabelFilterMenu;